import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useMarket } from './MarketContext';
import { getUserTransactions } from '../services/transactionService';
import { simulateFixedDCAStrategy } from '../services/reportService';

const BenchmarkContext = createContext();

/**
 * Context para compartilhar dados de benchmark entre páginas
 * Evita recalcular a mesma coisa em Reports e Portfolio
 */
export const BenchmarkProvider = ({ children }) => {
  const { user } = useAuth();
  const { bitcoinPrice } = useMarket();
  
  const [benchmarkData, setBenchmarkData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastCalculated, setLastCalculated] = useState(null);

  // Função para calcular benchmark
  const calculateBenchmark = useCallback(async (forceRefresh = false) => {
    // Evitar recálculo se já temos dados recentes (menos de 5 minutos)
    if (!forceRefresh && benchmarkData && lastCalculated) {
      const timeSinceLastCalc = Date.now() - lastCalculated;
      if (timeSinceLastCalc < 5 * 60 * 1000) {
        console.log('📊 [BenchmarkContext] Using cached benchmark data');
        return benchmarkData;
      }
    }

    if (!user?.username || !bitcoinPrice || bitcoinPrice <= 0) {
      console.log('📊 [BenchmarkContext] Missing required data', { user: !!user, bitcoinPrice });
      return null;
    }

    try {
      setIsLoading(true);
      setError(null);
      console.log('📊 [BenchmarkContext] Calculating benchmark data...');

      // Buscar transações
      const transactions = await getUserTransactions(user.username);
      
      if (!transactions || transactions.length === 0) {
        console.log('📊 [BenchmarkContext] No transactions found');
        setBenchmarkData(null);
        return null;
      }

      // Simular DCA Fixo — a função já calcula o ROI dinâmico corretamente
      // (considera vendas, caixa de vendas e usa btc_acquired do backend)
      const comparisonData = await simulateFixedDCAStrategy(
        transactions,
        bitcoinPrice,
        user.username
      );

      if (!comparisonData?.summary) {
        console.log('📊 [BenchmarkContext] No comparison data returned');
        setBenchmarkData(null);
        return null;
      }

      const s = comparisonData.summary;

      if (s.dynamicTotalInvested <= 0) {
        console.log('📊 [BenchmarkContext] No investment found');
        setBenchmarkData(null);
        return null;
      }

      // ROI dinâmico: usa valores já calculados pela simulação (inclui vendas e caixa)
      const dynamicROI = s.dynamicProfitLossPercentage;
      const dynamicAvgPrice = comparisonData.userStrategy?.averagePrice || 0;

      // ROI do DCA fixo
      const fixedDcaROI = s.fixedDcaProfitLossPercentage;
      const fixedDcaBtc = s.fixedDcaBtcBalance;
      const fixedDcaInvested = s.fixedDcaTotalInvested;
      const fixedDcaAvgPrice = comparisonData.fixedDCA?.averagePrice || 0;

      // HODL: compra única na primeira transação com o mesmo capital total
      const buyTransactions = transactions.filter(tx => tx.type === 'buy');
      const sortedBuyTxs = [...buyTransactions].sort((a, b) =>
        new Date(a.date) - new Date(b.date)
      );
      const firstTransaction = sortedBuyTxs[0];

      let hodlROI = 0;
      if (firstTransaction && firstTransaction.price > 0) {
        const btcIfHodl = s.dynamicTotalInvested / firstTransaction.price;
        const hodlValue = btcIfHodl * bitcoinPrice;
        hodlROI = ((hodlValue - s.dynamicTotalInvested) / s.dynamicTotalInvested) * 100;
      }

      const result = {
        dynamic: {
          roi: dynamicROI,
          value: s.dynamicCurrentValue,
          invested: s.dynamicTotalInvested,
          profitLoss: s.dynamicProfitLoss,
          avgPrice: dynamicAvgPrice,
          btc: s.dynamicBtcBalance
        },
        hodl: {
          roi: hodlROI,
          difference: dynamicROI - hodlROI
        },
        fixedDca: {
          roi: fixedDcaROI,
          difference: dynamicROI - fixedDcaROI,
          avgPrice: fixedDcaAvgPrice,
          btc: fixedDcaBtc
        },
        metadata: {
          totalTransactions: transactions.length,
          totalBuyOperations: buyTransactions.length,
          firstTransactionDate: firstTransaction?.date,
          bitcoinPrice: bitcoinPrice
        }
      };

      console.log('✅ [BenchmarkContext] Benchmark calculated successfully', result);
      setBenchmarkData(result);
      setLastCalculated(Date.now());
      return result;

    } catch (err) {
      console.error('❌ [BenchmarkContext] Error calculating benchmark:', err);
      setError(err.message || 'Failed to calculate benchmark');
      setBenchmarkData(null);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [user?.username, bitcoinPrice, benchmarkData, lastCalculated]);

  // Auto-calcular quando usuário ou preço mudam
  useEffect(() => {
    if (user?.username && bitcoinPrice > 0) {
      calculateBenchmark();
    }
  }, [user?.username, bitcoinPrice]); // Não incluir calculateBenchmark para evitar loop

  // Listener para evento de nova transação
  useEffect(() => {
    const handleTransactionEvent = () => {
      console.log('📊 [BenchmarkContext] Transaction event detected, recalculating...');
      calculateBenchmark(true); // Force refresh
    };

    window.addEventListener('transaction-created', handleTransactionEvent);
    window.addEventListener('transaction-deleted', handleTransactionEvent);

    return () => {
      window.removeEventListener('transaction-created', handleTransactionEvent);
      window.removeEventListener('transaction-deleted', handleTransactionEvent);
    };
  }, [calculateBenchmark]);

  const value = {
    benchmarkData,
    isLoading,
    error,
    calculateBenchmark,
    lastCalculated
  };

  return (
    <BenchmarkContext.Provider value={value}>
      {children}
    </BenchmarkContext.Provider>
  );
};

export const useBenchmark = () => {
  const context = useContext(BenchmarkContext);
  if (!context) {
    throw new Error('useBenchmark must be used within BenchmarkProvider');
  }
  return context;
};

export default BenchmarkContext;
